import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { defaultPatch } from "../lib/synthLab";
import LabShell from "../components/synthlab/LabShell";
import MidiSetupCard from "../components/synthlab/MidiSetupCard";
import {
  useSynthVoices,
  useMidiInput,
  loadCcMap,
  saveCcMap,
} from "../components/synthlab/useSynthInput";

// The MIDI-Learn screen: bind each on-screen knob to a physical control once,
// and every Dial/Fix practice round accepts the hardware as an answer method.
const SynthLabMidi = () => {
  const navigate = useNavigate();
  const [midiEnabled, setMidiEnabled] = useState(false);
  const [ccMap, setCcMap] = useState(loadCcMap);
  const [learning, setLearning] = useState(null);

  // Incoming notes echo through a neutral patch so the connection is audible.
  const [patch] = useState(() => defaultPatch());
  const { press, release } = useSynthVoices(patch);

  // Refs so the (stable) CC handler always sees the latest learn target.
  const learningRef = useRef(learning);
  learningRef.current = learning;

  // A CC arrived: either bind it to the knob being learned (kept 1:1 by
  // dropping the CC from any other knob), or ignore it here — the practice
  // and playground screens are the ones that consume bound knobs.
  const onControl = useCallback((controller) => {
    if (!learningRef.current) return;
    const paramId = learningRef.current;
    setCcMap((m) => {
      const next = {};
      Object.entries(m).forEach(([id, cc]) => {
        if (cc !== controller) next[id] = cc;
      });
      next[paramId] = controller;
      return next;
    });
    setLearning(null);
  }, []);

  const midi = useMidiInput(press, release, midiEnabled, onControl);

  useEffect(() => {
    saveCcMap(ccMap);
  }, [ccMap]);

  const startLearn = useCallback(
    (paramId) => {
      if (paramId && !midiEnabled) setMidiEnabled(true);
      setLearning(paramId);
    },
    [midiEnabled]
  );
  const clearBinding = useCallback((paramId) => {
    setCcMap((m) => {
      const next = { ...m };
      delete next[paramId];
      return next;
    });
  }, []);
  const resetBindings = useCallback(() => setCcMap({}), []);

  return (
    <LabShell
      eyebrow="hardware hookup"
      title="MIDI Setup"
      blurb="Optional — everything works by touch. But bind your synth's knobs once and practice rounds accept the real hardware as your answer."
    >
      <MidiSetupCard
        midiEnabled={midiEnabled}
        setMidiEnabled={setMidiEnabled}
        midi={midi}
        ccMap={ccMap}
        learning={learning}
        onLearn={startLearn}
        onClear={clearBinding}
        onResetAll={resetBindings}
        onBack={() => {
          setLearning(null);
          navigate("/synth-lab");
        }}
      />
    </LabShell>
  );
};

export default SynthLabMidi;
